import { useEffect, useState } from "react";
import { Play } from "lucide-react";
import { api, errMsg, fmtMoney } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Stamp, EmptyState, StatCard, Avatar } from "@/components/common";
import { PayslipDialog } from "@/pages/MyHR";
import { FormDialog } from "@/components/forms";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function Payroll() {
  const { can, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState("all");
  const [sel, setSel] = useState(null);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/payslips").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [user.active_org_id]); // eslint-disable-line
  const periods = [...new Set(rows.map((r) => r.period))].sort().reverse();
  const shown = rows.filter((r) => period === "all" || r.period === period);
  const total = shown.reduce((a, r) => a + r.net, 0);
  const nextPeriod = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; };

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Payroll & Finance" title="Payslips" description="Every slip generated for the organization, filed by period."
        actions={can("payroll.payslips", "create") && <Button className="rounded-full" data-testid="run-payroll-button" onClick={() => setOpen(true)}><Play size={14} className="mr-1" /> Run payroll</Button>} />
      <div className="grid grid-cols-3 gap-5 stagger">
        <StatCard label="Payslips" value={shown.length} hint={period === "all" ? "all periods" : period} testId="payroll-count" />
        <StatCard label="Net disbursed" value={fmtMoney(total)} accent="text-sage" testId="payroll-total" />
        <StatCard label="Periods" value={periods.length} hint="on record" accent="text-amber" testId="payroll-periods" />
      </div>
      <div className="flex gap-1 flex-wrap" data-testid="period-filter">{["all", ...periods].map((p) => <button key={p} data-testid={`period-${p}`} onClick={() => setPeriod(p)} className={`px-3 h-8 rounded-full text-xs border font-mono transition-colors ${period === p ? "bg-foreground text-background border-foreground" : "border-border bg-card hover:border-primary/50"}`}>{p}</button>)}</div>
      <div className="paper-card overflow-hidden rise" data-testid="payslip-list">
        {shown.length === 0 ? <EmptyState title="No payslips" hint="Run payroll to generate this period." /> : shown.map((p) => (
          <button key={p.id} data-testid={`payslip-row-${p.id}`} onClick={() => setSel(p)} className="ledger-row w-full text-left" style={{ gridTemplateColumns: "auto minmax(0,2fr) 1fr 1fr 1fr auto" }}>
            <Avatar name={p.user_name} size={30} />
            <div><div className="text-sm font-medium">{p.user_name}</div><div className="text-xs text-muted-foreground">{p.department}</div></div>
            <div className="font-mono text-xs text-muted-foreground">{p.period}</div>
            <div className="font-mono text-xs text-muted-foreground">tax {fmtMoney(p.tax)}</div>
            <div className="font-mono text-sm font-medium">{fmtMoney(p.net)}</div>
            <Stamp status={p.status} />
          </button>
        ))}
      </div>
      <PayslipDialog p={sel} onClose={() => setSel(null)} />
      <FormDialog open={open} onOpenChange={setOpen} title="Run payroll" submitLabel="Generate payslips" testId="run-payroll-dialog" initial={{ period: nextPeriod() }} fields={[{ name: "period", label: "Period (YYYY-MM)", required: true, full: true, hint: "Generates slips for every active employee without one for this period." }]}
        onSubmit={async (f) => { const { data } = await api.post("/payslips/run", f); toast.success(`${data.created} payslips generated`); load(); }} />
    </div>
  );
}
